import React from 'react';
import PropTypes from 'prop-types';
import { Field } from 'redux-form';
import { noop } from 'lodash';

import { Col, Row } from 'react-flexbox-grid';
import { Input, PhoneInput, Select, Textarea } from 'shared/components/reduxFormComponents';
import { ASTERISK } from 'shared/components/_constants/label';
import requestsStatuses from 'modules/common/constants/requestsStatuses';
import { removePlus } from 'modules/user/utils/formatPhoneNumber';
import DateOfBirth from 'modules/user/components/_forms/UserDetailsForm/components/DateOfBirth';
import { RestrictedUserField } from 'modules/appointmentsManagement/components/_forms/CreateAppointmentForm/components';

import { localErrorMessagesMap } from 'modules/appointmentsManagement/constants/localErrorCodes';
import { apiErrorMessagesMap } from 'modules/appointmentsManagement/constants/apiErrorCodes';
import { localErrorMessagesMap as userLocalErrorMessagesMap } from 'modules/user/constants/localErrorCodes';
import { apiErrorMessagesMap as userApiErrorMessagesMap } from 'modules/user/constants/apiErrorCodes';
import { userAttributeLabels } from 'modules/user/constants/userAttributeLabels';

import theme from './PatientDetailsForm.scss';

const propTypes = {
  form: PropTypes.string.isRequired,
  untouch: PropTypes.func.isRequired,
  changeFieldValue: PropTypes.func.isRequired,
  submitFormStatus: PropTypes.string.isRequired,
  userTitleOptions: PropTypes.array,
  userGenderOptions: PropTypes.array,
  fieldName: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
  formValues: PropTypes.object,
  viewportRef: PropTypes.func,
  readable: PropTypes.bool,
  userUpdatePermissions: PropTypes.object,
  userFieldConfig: PropTypes.object,
};

const defaultProps = {
  userTitleOptions: [],
  userGenderOptions: [],
  disabled: false,
  formValues: {},
  viewportRef: noop,
  readable: true,
  userUpdatePermissions: {
    canUpdateUserAccount: true,
    canUpdatePolicyId: true,
    canUpdateName: true,
    canUpdateGender: true,
    canUpdateBirthDate: true,
    canUpdateEmail: true,
    canUpdatePhone: true,
    canUpdateAdditionalPhone: true,
    canUpdateAddress: true,
    canUpdateDescription: true,
    canUpdateAvatar: true,
  },
  userFieldConfig: {
    requiredFields: [],
    userPhoneCountryCode: null,
  },
};

const errorMessagesMap = {
  ...apiErrorMessagesMap,
  ...localErrorMessagesMap,
  ...userLocalErrorMessagesMap,
  ...userApiErrorMessagesMap,
};

const CreateOrUpdatePatientForm = ({
  form,
  untouch,
  submitFormStatus,
  userTitleOptions,
  userGenderOptions,
  changeFieldValue,
  viewportRef,
  formValues,
  fieldName,
  disabled,
  readable,
  userUpdatePermissions,
  userFieldConfig,
}) => {
  return (
    <div className={ theme.root } ref={ viewportRef } >
      <Row>
        <Col xs={ 12 } sm={ 6 } className={ theme.fieldset }>
          <Field
            name={ `${ fieldName }.firstName` }
            testId="userName"
            component={ !readable ? RestrictedUserField : Input }
            label={ userAttributeLabels.firstName }
            labelSuffix={ userFieldConfig.requiredFields.includes('firstName') ? ASTERISK : undefined }
            placeholder={{
              id: 'MyAccount.Details.Edit.GivenName.Placeholder',
              defaultMessage: '** Type user’s given name',
            }}
            tooltipTestId="user-name-tooltip"
            tooltipSize="medium"
            errorMessagesMap={ errorMessagesMap }
            maxLength={ 200 }
            onBlur={ () => { untouch('firstName'); } }
            disabled={ disabled || !userUpdatePermissions.canUpdateName }
          />
        </Col>

        <Col xs={ 12 } sm={ 6 } className={ theme.fieldset }>
          <Field
            name={ `${ fieldName }.middleName` }
            testId="userMiddleName"
            component={ !readable ? RestrictedUserField : Input }
            label={ userAttributeLabels.middleName }
            labelSuffix={ userFieldConfig.requiredFields.includes('middleName') ? ASTERISK : undefined }
            placeholder={{
              id: 'MyAccount.Details.Edit.MiddleName.Placeholder',
              defaultMessage: '** Type user’s middle name',
            }}
            maxLength={ 200 }
            onBlur={ () => { untouch('middleName'); } }
            disabled={ disabled || !userUpdatePermissions.canUpdateName }
          />
        </Col>

        <Col xs={ 12 } sm={ 6 } className={ theme.fieldset }>
          <Field
            name={ `${ fieldName }.lastName` }
            testId="userLastName"
            component={ !readable ? RestrictedUserField : Input }
            label={ userAttributeLabels.lastName }
            labelSuffix={ userFieldConfig.requiredFields.includes('lastName') ? ASTERISK : undefined }
            placeholder={{
              id: 'MyAccount.Details.Edit.FamilyName.Placeholder',
              defaultMessage: '** Type user’s given name',
            }}
            tooltipSize="medium"
            tooltipTestId="user-last-name-tooltip"
            errorMessagesMap={ errorMessagesMap }
            maxLength={ 200 }
            onBlur={ () => { untouch('lastName'); } }
            disabled={ disabled || !userUpdatePermissions.canUpdateName }
          />
        </Col>

        <Col xs={ 12 } sm={ 6 } className={ theme.fieldset }>
          <Field
            name={ `${ fieldName }.title` }
            testId="userTitle"
            component={ !readable ? RestrictedUserField : Select }
            items={ userTitleOptions }
            label={ userAttributeLabels.title }
            labelSuffix={ userFieldConfig.requiredFields.includes('title') ? ASTERISK : undefined }
            placeholder={{
              id: 'MyAccount.Details.Edit.UserTitle.Placeholder',
              defaultMessage: '** Select title',
            }}
            loadingMessage={{
              id: 'const.pleaseWait',
              defaultMessage: '** Please Wait...',
            }}
            status={
              submitFormStatus === requestsStatuses.success
                ? requestsStatuses.success
                : requestsStatuses.default
            }
            clearable={ false }
            onBlur={ () => { untouch('title'); } }
            viewportRef={ viewportRef }
            disabled={ disabled || !userUpdatePermissions.canUpdateName }
          />
        </Col>

        <Col xs={ 12 } sm={ 6 } className={ theme.fieldset }>
          <Field
            name={ `${ fieldName }.gender` }
            testId="userGender"
            component={ !readable ? RestrictedUserField : Select }
            items={ userGenderOptions }
            label={ userAttributeLabels.gender }
            labelSuffix={ userFieldConfig.requiredFields.includes('gender') ? ASTERISK : undefined }
            placeholder={{
              id: 'MyAccount.Details.Edit.Gender.Placeholder',
              defaultMessage: '** Select user’s gender',
            }}
            loadingMessage={{
              id: 'const.pleaseWait',
              defaultMessage: '** Please Wait...',
            }}
            clearable={ false }
            onBlur={ () => { untouch('gender'); } }
            viewportRef={ viewportRef }
            disabled={ disabled || !userUpdatePermissions.canUpdateGender }
          />
        </Col>

        <Col xs={ 12 } sm={ 6 } className={ theme.fieldset }>
          <Field
            name={ `${ fieldName }.dateOfBirth` }
            testId="dateOfBirth"
            component={ !readable ? RestrictedUserField : DateOfBirth }
            label={ userAttributeLabels.dateOfBirth }
            labelSuffix={ userFieldConfig.requiredFields.includes('dateOfBirth') ? ASTERISK : undefined }
            errorMessagesMap={ errorMessagesMap }
            onBlur={ () => { untouch('dateOfBirth'); } }
            disabled={ disabled || !userUpdatePermissions.canUpdateBirthDate }
            dateFormat={ userFieldConfig.birthDateFormat }
          />
        </Col>

        <Col xs={ 12 } sm={ 6 } className={ theme.fieldset }>
          <Field
            name={ `${ fieldName }.email` }
            parse={ value => value.trim() }
            component={ !readable ? RestrictedUserField : Input }
            testId="userEmail"
            labelSuffix={ userFieldConfig.requiredFields.includes('email') ? ASTERISK : undefined }
            label={ userAttributeLabels.email }
            placeholder={{
              id: 'MyAccount.Details.Edit.Email.Placeholder',
              defaultMessage: '** Type user’s email address',
            }}
            disabled={ disabled || !userUpdatePermissions.canUpdateEmail }
            tooltipSize="medium"
            tooltipTestId="user-email-tooltip"
            errorMessagesMap={ errorMessagesMap }
            onBlur={ () => { untouch('email'); } }
            inputMode="email"
          />
        </Col>

        <Col xs={ 12 } sm={ 6 } className={ theme.fieldset }>
          <Field
            name={ `${ fieldName }.phoneNumber` }
            component={ !readable ? RestrictedUserField : PhoneInput }
            label={ userAttributeLabels.phoneNumber }
            labelSuffix={ userFieldConfig.requiredFields.includes('phoneNumber') ? ASTERISK : undefined }
            tooltipTestId="user-phone-tooltip"
            tooltipSize="medium"
            errorMessagesMap={ errorMessagesMap }
            onFocus={ ({ target: { value }, phoneInputField }, name) => {
              if (phoneInputField === 'countryCodeValue' && !value
                && ((formValues && !formValues.phoneNumber) || !formValues)
                && userFieldConfig.userPhoneCountryCode
              ) {
                changeFieldValue(form, `${ name }.countryCode`, removePlus(userFieldConfig.userPhoneCountryCode));
              }
            } }
            onBlur={ () => { untouch('phoneNumber'); } }
            disabled={ disabled || !userUpdatePermissions.canUpdatePhone }
          />
        </Col>

        <Col xs={ 12 } sm={ 6 } className={ theme.fieldset }>
          <Field
            name={ `${ fieldName }.externalId` }
            component={ !readable ? RestrictedUserField : Input }
            testId="externalId"
            label={ userFieldConfig.externalIdLabel }
            labelSuffix={ userFieldConfig.requiredFields.includes('externalId') ? ASTERISK : undefined }
            placeholder={ userFieldConfig.externalIdPlaceholder }
            maxLength={ 15 }
            validatePattern="^[0-9]"
            tooltipTestId="user-id-tooltip"
            tooltipSize="medium"
            errorMessagesMap={ errorMessagesMap }
            onBlur={ () => { untouch('externalId'); } }
            disabled={ disabled || !userUpdatePermissions.canUpdatePolicyId }
            inputMode="numeric"
          />
        </Col>

        <Col xs={ 12 } sm={ 6 } className={ theme.fieldset }>
          <Field
            name={ `${ fieldName }.address` }
            testId="user-address"
            component={ !readable ? RestrictedUserField : Textarea }
            rootClassName={ theme.textarea }
            label={ userAttributeLabels.address }
            labelSuffix={ userFieldConfig.requiredFields.includes('address') ? ASTERISK : undefined }
            placeholder={{
              id: 'MyAccount.Details.Edit.Address.Placeholder',
              defaultMessage: '** Type user’s Address',
            }}
            status={
              submitFormStatus === requestsStatuses.success
                ? requestsStatuses.success
                : requestsStatuses.default
            }
            onBlur={ () => { untouch('address'); } }
            autoExpandable
            disabled={ disabled || !userUpdatePermissions.canUpdateAddress }
          />
        </Col>

        <Col xs={ 12 } sm={ 6 } className={ theme.fieldset }>
          <Field
            name={ `${ fieldName }.description` }
            testId="profile-title"
            component={ !readable ? RestrictedUserField : Textarea }
            rootClassName={ theme.textarea }
            label={ userAttributeLabels.description }
            labelSuffix={ userFieldConfig.requiredFields.includes('description') ? ASTERISK : undefined }
            placeholder={{
              id: 'MyAccount.Details.Edit.Profile.Placeholder',
              defaultMessage: '** Type user’s Profile',
            }}
            status={
              submitFormStatus === requestsStatuses.success
                ? requestsStatuses.success
                : requestsStatuses.default
            }
            maxLength={ 200 }
            onBlur={ () => { untouch('description'); } }
            disabled={ disabled || !userUpdatePermissions.canUpdateDescription }
          />
        </Col>

      </Row>
    </div>
  );
};

CreateOrUpdatePatientForm.propTypes = propTypes;
CreateOrUpdatePatientForm.defaultProps = defaultProps;

export default CreateOrUpdatePatientForm;
